import React from "react";

const SimpleMap = ({ lat, lng, zoom = 15, className }) => {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markerRef = React.useRef(null);
  const checkIntervalRef = React.useRef(null);
  const embedTimeoutRef = React.useRef(null);
  const maxTimeoutRef = React.useRef(null);
  const loadStartedRef = React.useRef(false);
  // Track if we've already initialized to prevent double initialization
  const initializedRef = React.useRef(false);
  // Track previous coordinates to detect actual changes
  const prevCoordsRef = React.useRef({ lat: null, lng: null, zoom: null });

  const [mode, setMode] = React.useState("loading"); // 'loading' | 'interactive' | 'embed'

  // Get the legacy Marker constructor (doesn't require Map ID)
  const getLegacyMarkerConstructor = () => {
    if (typeof window === "undefined") return null;
    return window.google?.maps?.Marker || null;
  };

  const hasMapConstructor = () => {
    if (typeof window === "undefined") return false;
    const g = window.google;
    return !!(g && g.maps && typeof g.maps.Map === "function");
  };

  // Memoized initialization function that guards against double-init
  const initInteractiveMap = React.useCallback(() => {
    if (!containerRef.current || !hasMapConstructor()) return false;

    try {
      // If map doesn't exist, create it
      if (!mapRef.current) {
        mapRef.current = new window.google.maps.Map(containerRef.current, {
          center: { lat, lng },
          zoom: zoom || 15,
          mapTypeId: "roadmap",
          zoomControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
        });
        initializedRef.current = true;
        prevCoordsRef.current = { lat, lng, zoom };
      } else {
        // Only update if coordinates actually changed
        const prev = prevCoordsRef.current;
        const coordsChanged = prev.lat !== lat || prev.lng !== lng;
        const zoomChanged = prev.zoom !== zoom;

        if (coordsChanged) {
          mapRef.current.setCenter({ lat, lng });
        }
        if (zoomChanged && zoom) {
          mapRef.current.setZoom(zoom);
        }

        if (coordsChanged || zoomChanged) {
          prevCoordsRef.current = { lat, lng, zoom };
        }
      }

      // Use legacy Marker which doesn't require a Map ID
      // AdvancedMarkerElement requires a mapId to be set on the map,
      // which needs Google Cloud Console configuration
      const syncMarker = () => {
        const MarkerConstructor = getLegacyMarkerConstructor();
        if (!MarkerConstructor) return;

        if (!markerRef.current) {
          markerRef.current = new MarkerConstructor({
            position: { lat, lng },
            map: mapRef.current,
            title: "Business Location",
          });
          return;
        }

        // Only update marker position if coordinates changed
        const currentPos = markerRef.current.getPosition();
        if (
          !currentPos ||
          currentPos.lat() !== lat ||
          currentPos.lng() !== lng
        ) {
          markerRef.current.setPosition({ lat, lng });
        }
        if (markerRef.current.getMap() !== mapRef.current) {
          markerRef.current.setMap(mapRef.current);
        }
      };

      syncMarker();

      // Only set mode if not already interactive
      if (mode !== "interactive") {
        setMode("interactive");
      }
      return true;
    } catch (err) {
      console.warn("SimpleMap initialization/update failed:", err);
      return false;
    }
  }, [lat, lng, zoom, mode]);

  const loadGoogleMapsIfNeeded = React.useCallback(() => {
    if (typeof window === "undefined") return;
    if (hasMapConstructor()) return;
    if (loadStartedRef.current) return;
    loadStartedRef.current = true;

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return; // can't load without key; embed fallback will handle

    window.googleAsyncInit = () => {
      // Poll until Map constructor is available
    };

    if (!window.document.getElementById("__googleMapsScriptId")) {
      const js = window.document.createElement("script");
      js.id = "__googleMapsScriptId";
      js.async = true;
      js.defer = true;
      // Removed 'marker' library since we're using legacy Marker
      js.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,visualization,drawing&callback=googleAsyncInit&loading=async`;
      window.document.body.appendChild(js);
    }
  }, []);

  // Manage loading, fallback to embed, and upgrading to interactive when ready
  React.useEffect(() => {
    if (typeof window === "undefined") return () => {};

    // If already in interactive mode with a valid map, skip re-initialization
    if (mode === "interactive" && mapRef.current && initializedRef.current) {
      return () => {};
    }

    // If maps is ready now and not yet initialized, init immediately
    if (!initializedRef.current && initInteractiveMap()) {
      return () => {};
    }

    // Start script load/polling only if not yet loaded
    loadGoogleMapsIfNeeded();

    // Only start polling if not already initialized
    if (!initializedRef.current && !checkIntervalRef.current) {
      // Poll every 250ms for up to 30s to upgrade to interactive
      checkIntervalRef.current = window.setInterval(() => {
        if (hasMapConstructor() && !initializedRef.current) {
          const ok = initInteractiveMap();
          if (ok && checkIntervalRef.current) {
            window.clearInterval(checkIntervalRef.current);
            checkIntervalRef.current = null;
          }
        }
      }, 250);

      // After 5s, show embed fallback so we never spin forever
      embedTimeoutRef.current = window.setTimeout(() => {
        if (mode !== "interactive" && !initializedRef.current) {
          setMode("embed");
        }
      }, 5000);

      // After 30s stop polling to avoid leaks
      maxTimeoutRef.current = window.setTimeout(() => {
        if (checkIntervalRef.current) {
          window.clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      }, 30000);
    }

    return () => {
      if (checkIntervalRef.current) {
        window.clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (embedTimeoutRef.current) window.clearTimeout(embedTimeoutRef.current);
      if (maxTimeoutRef.current) window.clearTimeout(maxTimeoutRef.current);
    };
  }, [initInteractiveMap, loadGoogleMapsIfNeeded, mode]);

  // Keep marker/map in sync when props change while interactive
  React.useEffect(() => {
    if (mode !== "interactive" || !initializedRef.current) return;

    // Check if coordinates actually changed before updating
    const prev = prevCoordsRef.current;
    if (prev.lat !== lat || prev.lng !== lng || prev.zoom !== zoom) {
      initInteractiveMap();
    }
  }, [lat, lng, zoom, mode, initInteractiveMap]);

  const z = zoom || 15;
  const embedSrc = `https://maps.google.com/maps?q=${encodeURIComponent(lat)},${encodeURIComponent(lng)}&z=${encodeURIComponent(z)}&output=embed`;

  return (
    <div className={className || "relative h-full w-full"}>
      <div
        ref={containerRef}
        className="h-full w-full"
        style={{ visibility: mode === "interactive" ? "visible" : "hidden" }}
      />

      {mode === "loading" && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-transparent text-gray-400"
          role="status"
          aria-live="polite"
        >
          <div className="text-center">
            <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500"></div>
            <p className="text-xs sm:text-sm">Loading map...</p>
          </div>
        </div>
      )}

      {mode === "embed" && (
        <iframe
          title="Map"
          data-provider-identity="google-maps"
          src={embedSrc}
          className="absolute inset-0 h-full w-full"
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      )}
    </div>
  );
};

export default SimpleMap;
